/**
 * @fileoverview Modal for assigning projects to students
 */
import { Flex } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconUsersGroup, IconUsersPlus } from '@tabler/icons-react'
import { Box, Button, Center, ChangedDataConfirmModal, EmptyStateBox, Group, Modal, Skeleton, Stack } from 'components'
import { useArcTranslation } from 'hooks'
import {
  AddStudentsModal,
  AddStudentsModalProvider,
  useAddStudentsModal,
} from 'pages/TeacherDashboard/components/AddStudentsModal'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { AssignProjectProvider, useAssignProjectContext } from './AssignProjectContext'
import { DateGroupFields } from './DateGroupFields'
import { ModalFooter } from './ModalFooter'
import { StudentsPanel } from './StudentsPanel'
import { getModalStyles, SPACING } from './styles'
import type { AssignProjectModalProps, ProjectGroup, Student } from './types'
import { useAssignProjectData } from './useAssignProjectData'
import { useResponsive } from './useResponsive'
import { useStudentFilters } from './useStudentFilters'

interface ModalContentProps extends Omit<AssignProjectModalProps, 'opened'> {
  onRequestClose: () => void
  fetchedGroups: ProjectGroup[]
  fetchedStudents: Student[]
  dataLoading: boolean
  refetch: () => void
}

const EmptyGroupsState: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useArcTranslation()
  const { open: openAddStudents } = useAddStudentsModal()

  const handleAddStudents = useCallback(() => {
    onClose()
    openAddStudents()
  }, [onClose, openAddStudents])

  return (
    <Center sx={{ flex: 1 }} p={40}>
      <EmptyStateBox
        text={t('assign_project.no_groups_empty_state')}
        icon={<IconUsersGroup size={32} color='#999' />}
        actionButton={
          <Button
            variant='filled'
            color='teal'
            radius='xl'
            leftIcon={<IconUsersPlus size={18} />}
            onClick={handleAddStudents}
          >
            {t('assign_project.add_students_button')}
          </Button>
        }
      />
    </Center>
  )
}

const ModalContent: FC<ModalContentProps> = ({
  onClose,
  onSave,
  projectId,
  groups: propsGroups,
  students: propsStudents,
  isLoading = false,
  onRequestClose,
  fetchedGroups,
  fetchedStudents,
  dataLoading,
}) => {
  const { isMobile, modalPadding } = useResponsive()
  const ctx = useAssignProjectContext()

  const groups = propsGroups && propsGroups.length > 0 ? propsGroups : fetchedGroups
  const students = propsStudents && propsStudents.length > 0 ? propsStudents : fetchedStudents

  const { availableStudents, assignedStudents, totalInGroup } = useStudentFilters({
    students,
    assignedStudentIds: ctx.assignedStudentIds,
    searchQuery: ctx.searchQuery,
    filterGroupId: ctx.filterGroupId,
  })

  const firstGroupId = groups[0]?.id
  const effectiveGroupId = useMemo(
    () => (groups.length === 1 ? firstGroupId : ctx.selectedGroupId),
    [groups.length, firstGroupId, ctx.selectedGroupId],
  )

  // Auto-select group when there's only one available (silent to not mark as dirty)
  useEffect(() => {
    if (groups.length === 1 && firstGroupId && ctx.selectedGroupId !== firstGroupId) {
      ctx.setSelectedGroupId(firstGroupId, { silent: true })
    }
  }, [groups.length, firstGroupId, ctx.selectedGroupId, ctx.setSelectedGroupId])

  const isFormValid = ctx.startDate !== null && effectiveGroupId !== null
  const showSubmitOverlay = ctx.isSubmitting || isLoading

  const handleSave = useCallback(async () => {
    ctx.setIsSubmitting(true)
    const dataToSave = {
      projectId,
      startDate: ctx.startDate,
      groupId: effectiveGroupId,
      assignedStudentIds: ctx.assignedStudentIds,
    }
    try {
      await onSave(dataToSave)
    } finally {
      ctx.setIsSubmitting(false)
    }
  }, [projectId, effectiveGroupId, onSave, ctx.startDate, ctx.assignedStudentIds, ctx.setIsSubmitting])

  if (dataLoading) {
    return (
      <Flex direction='column' sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
        <Box px={modalPadding} py={SPACING.section}>
          <Group grow spacing={SPACING.element} noWrap>
            <Skeleton height={68} radius='md' animate />
            <Skeleton height={68} radius='md' animate />
          </Group>
        </Box>
        <Group spacing={SPACING.element} noWrap px={modalPadding} pb={SPACING.section} sx={{ flex: 1 }} mih={0}>
          <Skeleton sx={{ flex: 1, alignSelf: 'stretch' }} radius='md' animate />
          <Skeleton sx={{ flex: 1, alignSelf: 'stretch' }} radius={12} animate />
        </Group>
      </Flex>
    )
  }

  if (groups.length === 0) {
    return <EmptyGroupsState onClose={onClose} />
  }

  return (
    <Flex direction='column' sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
      <Flex
        direction='column'
        px={modalPadding}
        py={SPACING.section}
        sx={{
          flex: 1,
          overflow: 'hidden',
          opacity: showSubmitOverlay ? 0.5 : 1,
          pointerEvents: showSubmitOverlay ? 'none' : 'auto',
        }}
        mih={0}
      >
        <Stack spacing={SPACING.section} sx={{ flex: 1, overflow: 'hidden' }} mih={0}>
          <DateGroupFields groups={groups} isMobile={isMobile} />
          <StudentsPanel
            availableStudents={availableStudents}
            assignedStudents={assignedStudents}
            totalStudents={totalInGroup}
            groups={groups}
            isMobile={isMobile}
          />
        </Stack>
      </Flex>
      <ModalFooter
        isMobile={isMobile}
        modalPadding={modalPadding}
        isFormValid={isFormValid}
        isLoading={showSubmitOverlay}
        onCancel={onRequestClose}
        onSave={handleSave}
      />
    </Flex>
  )
}

const ModalWrapper: FC<AssignProjectModalProps> = props => {
  const { opened, projectId } = props
  const { isMobile, modalPadding, modalSize } = useResponsive()
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false)

  const {
    groups: fetchedGroups,
    students: fetchedStudents,
    isLoading: dataLoading,
    refetch,
  } = useAssignProjectData({
    projectId,
    opened,
  })

  return (
    <AssignProjectProvider opened={opened}>
      <AddStudentsModalProvider onStudentsAdded={refetch}>
        <ModalWrapperInner
          {...props}
          isMobile={isMobile}
          modalPadding={modalPadding}
          modalSize={modalSize}
          confirmOpened={confirmOpened}
          openConfirm={openConfirm}
          closeConfirm={closeConfirm}
          fetchedGroups={fetchedGroups}
          fetchedStudents={fetchedStudents}
          dataLoading={dataLoading}
          refetch={refetch}
        />
        <AddStudentsModal />
      </AddStudentsModalProvider>
    </AssignProjectProvider>
  )
}

interface ModalWrapperInnerProps extends AssignProjectModalProps {
  isMobile: boolean
  modalPadding: number
  modalSize: number | string
  confirmOpened: boolean
  openConfirm: () => void
  closeConfirm: () => void
  fetchedGroups: ProjectGroup[]
  fetchedStudents: Student[]
  dataLoading: boolean
  refetch: () => void
}

const ModalWrapperInner: FC<ModalWrapperInnerProps> = ({
  opened,
  onClose,
  isMobile,
  modalPadding,
  modalSize,
  confirmOpened,
  openConfirm,
  closeConfirm,
  fetchedGroups,
  fetchedStudents,
  dataLoading,
  refetch,
  ...rest
}) => {
  const { t } = useArcTranslation()
  const ctx = useAssignProjectContext()
  // AR-670: the Classroom step moved out of the assign flow — linking is now a
  // standalone action on the project page (ProjectClassroomField), after assign.
  const hasUnsavedChanges = ctx.hasChanges

  const handleRequestClose = useCallback(() => {
    hasUnsavedChanges ? openConfirm() : onClose()
  }, [hasUnsavedChanges, openConfirm, onClose])

  const handleConfirmDiscard = useCallback(() => {
    closeConfirm()
    ctx.reset()
    onClose()
  }, [closeConfirm, ctx.reset, onClose])

  const modalStyles = getModalStyles(isMobile, modalPadding)
  const showSubmitOverlay = ctx.isSubmitting || !!rest.isLoading

  return (
    <>
    <Modal
      opened={opened}
      onClose={handleRequestClose}
      closeOnClickOutside={!hasUnsavedChanges && !showSubmitOverlay}
      closeOnEscape={!hasUnsavedChanges && !showSubmitOverlay}
      title={t('assign_project.title')}
      size={modalSize}
      fullScreen={isMobile}
      centered={!isMobile}
      padding={0}
      radius={isMobile ? 0 : 'lg'}
      styles={modalStyles}
    >
      <ModalContent
        {...rest}
        onClose={onClose}
        onRequestClose={handleRequestClose}
        fetchedGroups={fetchedGroups}
        fetchedStudents={fetchedStudents}
        dataLoading={dataLoading}
        refetch={refetch}
      />
      <ChangedDataConfirmModal opened={confirmOpened} close={closeConfirm} discard={handleConfirmDiscard} />
    </Modal>
    </>
  )
}

export const AssignProjectModal: FC<AssignProjectModalProps> = ModalWrapper
